import Head from 'next/head';
import { AdsView } from '@/views/Ads/Ads.view';
import { withPreRenderSettings } from '@/utils/hocs';
import { AdView } from '@/views/Ads/Ad.view';
import { EditAdView } from '@/views/Ads/EditAd.view';
import { getSeoFullData } from '@/api/helpers/seo.helpers';
import { ServerDataProvider } from '@/contexts';
import {
  getNewAdDefaultValues,
  getSearchResultsServerData,
  getServerAdDetails,
} from '@/api/helpers/ad.helpers';
import { AdDetails, NewAdDefaultValues } from '@/api/types/ads.types';
import { NewAdView } from '@/views/Ads/NewAd.view';
import NoSsr from '@/components/NoSsr/NoSsr';
import { usePageType } from '@/hooks/usePageType';
import { PageType } from '@/hooks/usePageType/usePageType.types';
import {
  CATEGORIES_SLUGS_COUNT,
  QUERY,
  ROOT_CATEGORY_SLUG,
} from '@/constants/common';
import {
  getCurrentCategory,
  getCurrentCategoryId,
} from '@/api/helpers/categories.helpers';
import {
  EDIT_AD_PAGE_SLUG,
  EDIT_AD_PAGE_SLUG_INDEX,
  LEGACY_ID_SLUG_INDEX,
  NEW_AD_PAGE_SLUG,
} from '@/hooks/usePageType/usePageType.constants';
import { SeoFullData } from '@/api/types/seo.types';
import { CategoryBaseInfo } from '@/api/types/categories.types';
import { getRegionsCached } from '@/api/helpers/regions.helpers';

interface Props {
  adDetails: AdDetails | null;
  hasAdNotFoundError?: boolean;
  hasInitialMissingAd?: boolean;
  refferer: string;
  newAdDefaultValues: NewAdDefaultValues | null;
  currentCategory: CategoryBaseInfo | null;
  seoFullData: SeoFullData | null;
}

function AdsPage({
  adDetails,
  hasAdNotFoundError,
  hasInitialMissingAd,
  newAdDefaultValues,
  currentCategory,
  seoFullData,
}: Props) {
  const pageType = usePageType();
  const { h1, title } = seoFullData || {};

  switch (pageType) {
    case PageType.NewAd:
      return (
        <NoSsr>
          <NewAdView {...newAdDefaultValues} />
        </NoSsr>
      );
    case PageType.EditAd:
      return (
        <ServerDataProvider adDetails={adDetails}>
          <EditAdView />
        </ServerDataProvider>
      );
    case PageType.Ad:
      return (
        <ServerDataProvider
          adDetails={adDetails}
          hasAdNotFoundError={hasAdNotFoundError}
          hasInitialMissingAd={hasInitialMissingAd}
        >
          <Head>
            <meta
              name="robots"
              content="max-image-preview:large"
              key="max-image-preview"
            />
          </Head>

          <AdView />
        </ServerDataProvider>
      );
    default:
      return (
        <ServerDataProvider currentCategory={currentCategory} h1={h1 || title}>
          <AdsView />
        </ServerDataProvider>
      );
  }
}

AdsPage.getInitialProps = withPreRenderSettings(
  async (context, client, deviceType) => {
    const isServer = !!context.req;
    const pathname = context.asPath?.split('?')[0].slice(1) || '';
    const { slug } = context.query;

    const isSearchResultsPage =
      pathname.split('/').length <= CATEGORIES_SLUGS_COUNT &&
      !pathname.endsWith(NEW_AD_PAGE_SLUG);
    const isNewAdPage = pathname.endsWith(`/${NEW_AD_PAGE_SLUG}`);
    const isRootCategory = pathname === ROOT_CATEGORY_SLUG;
    const isEditPage = slug?.[EDIT_AD_PAGE_SLUG_INDEX] === EDIT_AD_PAGE_SLUG;
    const legacyId = slug?.[LEGACY_ID_SLUG_INDEX] as string;
    const isAdPage =
      !isEditPage && !isSearchResultsPage && !Number.isNaN(+legacyId);

    const [currentCategory, regionsData] = await Promise.all([
      isAdPage
        ? getCurrentCategoryId(client, context)
        : getCurrentCategory(client, context),
      isSearchResultsPage || isNewAdPage
        ? getRegionsCached(client, context)
        : null,
    ]);

    if (
      isSearchResultsPage &&
      !currentCategory?.id &&
      !isRootCategory &&
      context.res
    ) {
      context.res.statusCode = 404;
      return {
        seoFullData: null,
        adDetails: null,
        newAdDefaultValues: null,
        hasAdNotFoundError: false,
      };
    }

    const isUserAdsPage = !!context.query[QUERY.userId];
    const isVipPage = pathname.endsWith('/vip');

    const [
      seoFullData,
      { adDetails, hasAdNotFoundError, hasInitialMissingAd },
      newAdDefaultValues,
    ] = await Promise.all([
      getSeoFullData(client, context, currentCategory?.id, regionsData),
      getServerAdDetails(client, context, deviceType),
      getNewAdDefaultValues(client, context, regionsData),

      isSearchResultsPage && !isUserAdsPage && isServer
        ? getSearchResultsServerData(
            client,
            context,
            deviceType,
            currentCategory as CategoryBaseInfo,
            regionsData,
            isVipPage,
            isRootCategory
          )
        : null,
    ]);

    return {
      seoFullData: seoFullData ?? null,
      adDetails: adDetails ?? null,
      hasAdNotFoundError,
      hasInitialMissingAd,
      newAdDefaultValues: newAdDefaultValues ?? null,
      currentCategory: currentCategory ?? null,
    };
  }
);

export default AdsPage;
